import React, { Suspense, lazy, useState } from 'react';
import { Button, Icon, Modal } from '../../components';
import RoomsList from './RoomsList';
import UserMiniCard from './UserMiniCard';
import { useIdentity } from '../../hooks/useIdentity';
import BeamGlobInfo from '../beam/BeamGlobInfo';
import './DesktopSidebar.css';

// Modals are lazy — the desktop sidebar mounts on every chat session but
// users don't always open the party / profile UI, so we don't pay their
// cost (PartyModal pulls qrcode.react ~7KB; ProfileDrawer is its own chunk)
// until the first interaction.
const PartyModal = lazy(() => import('../party/PartyModal'));
const ProfileDrawer = lazy(() => import('../user/ProfileDrawer'));
// Beam workspace — full-screen overlay opened from the Beam sidebar entry.
const DesktopBeam = lazy(() => import('./DesktopBeam'));

/**
 * DesktopSidebar — 260px left rail visible at ≥ 900px.
 *
 * Top: wordmark + version chip
 * Middle: Throw / Join split row + Rooms list
 * Bottom: UserMiniCard
 *
 * Owns the open/close state for the PartyModal (default Throw vs Join tab
 * routed via initialTab) and the ProfileDrawer. The on*Click props remain
 * for tests / future composition.
 */
const DesktopSidebar = ({
  onThrowClick,
  onJoinClick,
  onUserClick,
  onRerollClick,
  onSelectRoom,
  onBeamClick,
  onCollapse,
}) => {
  const { isInGlob } = useIdentity();

  const [partyOpen, setPartyOpen] = useState(false);
  const [partyTab, setPartyTab] = useState('throw');
  const [profileOpen, setProfileOpen] = useState(false);
  const [beamOpen, setBeamOpen] = useState(false);
  // GLOB intercept: tapping Beam in #GLOB opens an explainer popup with
  // throw/join CTAs instead of the (empty-because-no-mesh-in-GLOB) workspace.
  const [globInfoOpen, setGlobInfoOpen] = useState(false);

  const throwHandler = () => {
    if (onThrowClick) onThrowClick();
    setPartyTab('throw');
    setPartyOpen(true);
  };
  const joinHandler = () => {
    if (onJoinClick) onJoinClick();
    setPartyTab('join');
    setPartyOpen(true);
  };
  const userHandler = () => {
    if (onUserClick) onUserClick();
    setProfileOpen(true);
  };
  const beamHandler = () => {
    if (onBeamClick) onBeamClick();
    if (isInGlob) {
      setGlobInfoOpen(true);
    } else {
      setBeamOpen(true);
    }
  };
  // GLOB-info CTAs reuse the existing PartyModal flow.
  const handleGlobThrow = () => {
    setGlobInfoOpen(false);
    setPartyTab('throw');
    setPartyOpen(true);
  };
  const handleGlobJoin = () => {
    setGlobInfoOpen(false);
    setPartyTab('join');
    setPartyOpen(true);
  };

  return (
    <>
      <aside className="dsidebar" aria-label="Glacier sidebar">
        <div className="dsidebar__header">
          <span className="dsidebar__wordmark">glacier</span>
          {onCollapse && (
            <button
              type="button"
              className="dsidebar__collapse"
              onClick={onCollapse}
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
            >
              <Icon name="menu" size={14} aria-hidden />
            </button>
          )}
        </div>

        <div className="dsidebar__cta-row">
          <Button
            variant="compact"
            className="dsidebar__cta dsidebar__cta--throw"
            onClick={throwHandler}
          >
            <Icon name="plus" size={14} />
            Throw
          </Button>
          <Button
            variant="compact"
            className="dsidebar__cta dsidebar__cta--join"
            onClick={joinHandler}
          >
            <Icon name="link" size={14} />
            Join
          </Button>
        </div>

        <RoomsList onSelectRoom={onSelectRoom} />

        <Button
          variant="ghost"
          className="dsidebar__beam"
          onClick={beamHandler}
          aria-label="Open Beam — nearby devices"
        >
          <Icon name="radar" size={14} />
          Beam
        </Button>

        <UserMiniCard onUserClick={userHandler} onRerollClick={onRerollClick} />
      </aside>

      <Suspense fallback={null}>
        {partyOpen && (
          <PartyModal
            isOpen
            onClose={() => setPartyOpen(false)}
            initialTab={partyTab}
          />
        )}
        {profileOpen && (
          <ProfileDrawer isOpen onClose={() => setProfileOpen(false)} />
        )}
        {beamOpen && (
          <Modal
            isOpen
            onClose={() => setBeamOpen(false)}
            variant="card"
            size="full"
            ariaLabel="Beam workspace"
            className="dsidebar__beam-modal"
            bare
          >
            <DesktopBeam onClose={() => setBeamOpen(false)} />
          </Modal>
        )}
        {globInfoOpen && (
          <Modal
            isOpen
            onClose={() => setGlobInfoOpen(false)}
            variant="card"
            size="sm"
            title="Beam"
            ariaLabel="Beam — party-only"
          >
            <BeamGlobInfo
              onThrowParty={handleGlobThrow}
              onJoinParty={handleGlobJoin}
            />
          </Modal>
        )}
      </Suspense>
    </>
  );
};

export default DesktopSidebar;
